import React, { useState, useEffect } from 'react';
import { useProperty } from '../context/PropertyContext';
import { useAuth } from '../context/AuthContext';
import PropertyForm from './PropertyForm';
import PropertyDetailModal from './PropertyDetailModal';
import PropertyTypeSelector from './PropertyTypeSelector';
import DeactivatePropertyModal from './DeactivatePropertyModal';
import { BulkPropertyImport } from './BulkPropertyImport';
import DeletePropertyModal from './DeletePropertyModal';

import { Property } from '../types';
import { usePermissions } from '../hooks/usePermissions';
import { useUser } from '../context/UserContext';
import { supabase } from '../integrations/supabase/client';
import { formatPhone } from '../utils/masks';
import { 
  Plus, 
  Search, 
  Edit, 
  Trash2, 
  Eye,
  Share2,
  MapPin,
  Bed,
  Car,
  Ruler,
  Calendar,
  Grid3X3,
  List,
  Power,
  PowerOff,
  Upload
} from 'lucide-react';
import { useToast } from '../hooks/use-toast';

const PropertyList: React.FC = () => {
  console.log('🏠 PropertyList: Inicializando componente...');
  
  try {
    const { properties, saveProperty, updateProperty, deleteProperty, refreshProperties } = useProperty();
    
    console.log('🏠 PropertyList: Propriedades recebidas:', properties.length);
    console.log('🏠 PropertyList: Primeira propriedade:', properties[0]?.title, properties[0]?.transactionType, properties[0]?.isActive);
  const { canEditProperty, canDeleteProperty, isCapturer } = usePermissions();
  const { getUserById } = useUser();
  const { user } = useAuth();
  const { toast } = useToast();
  
  // Removido realtime listener duplicado - já está sendo tratado no PropertyContext
  
  
  // Estados do componente
  const [showForm, setShowForm] = useState(false);
  const [editingProperty, setEditingProperty] = useState<Property | undefined>();
  const [showTypeSelector, setShowTypeSelector] = useState(false);
  const [selectedPropertyType, setSelectedPropertyType] = useState<string>('');
  const [selectedCondominiumId, setSelectedCondominiumId] = useState<string | undefined>();
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('list');
  const [viewingProperty, setViewingProperty] = useState<Property | undefined>();
  
  // Estados para filtros
  const [statusTab, setStatusTab] = useState<'active' | 'inactive'>('active');
  const [typeTab, setTypeTab] = useState<'all' | 'sale' | 'rent'>('all');

  // Garantir dados atualizados ao abrir a aba de Inativos
  useEffect(() => {
    if (statusTab === 'inactive') {
      refreshProperties();
    }
  }, [statusTab]); // Removido refreshProperties das dependências para evitar loop
  const [selectedProperties, setSelectedProperties] = useState<Set<string>>(new Set());
  const [showDeactivateModal, setShowDeactivateModal] = useState(false);
  const [propertyToDeactivate, setPropertyToDeactivate] = useState<Property | null>(null);
  const [isBulkAction, setIsBulkAction] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [propertyToDelete, setPropertyToDelete] = useState<Property | null>(null);
  const [isBulkDeleteAction, setIsBulkDeleteAction] = useState(false);

  // Estados para filtros de busca
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    priceMin: 0,
    priceMax: 0,
    bedrooms: 0,
    parkingSpaces: 0,
    areaMin: 0,
    areaMax: 0,
    propertyType: ''
  });

  // Estado para import em lote
  const [showBulkImport, setShowBulkImport] = useState(false);

  // Filtrar propriedades
  const filteredProperties = properties.filter(property => {
    const searchLower = searchTerm.toLowerCase();
    const matchesSearch = property.title.toLowerCase().includes(searchLower) ||
                         property.location.neighborhood.toLowerCase().includes(searchLower) ||
                         property.location.city.toLowerCase().includes(searchLower) ||
                         property.propertyCode.toLowerCase().includes(searchLower) ||
                         property.listingId.toLowerCase().includes(searchLower);
    
    // Primeiro filtrar por status (ativo/inativo)
    const matchesStatus = statusTab === 'active' ? property.isActive : !property.isActive;
    
    // Depois filtrar por tipo de transação se não for 'all'
    let matchesType = true;
    if (typeTab === 'sale') {
      matchesType = (property.listPrice && property.listPrice > 0) ||
                   property.transactionType === 'Venda' ||
                   property.transactionType === 'Venda/Aluguel';
    } else if (typeTab === 'rent') {
      matchesType = (property.rentalPrice && property.rentalPrice > 0) ||
                   property.transactionType === 'Aluguel' ||
                   property.transactionType === 'Venda/Aluguel';
    }

    // Aplicar filtros de busca
    const price = (typeTab === 'rent')
      ? (property.rentalPrice || 0)
      : property.listPrice;
    const matchesFilters = 
      (!filters.priceMin || price >= filters.priceMin) &&
      (!filters.priceMax || price <= filters.priceMax) &&
      (!filters.bedrooms || property.bedrooms >= filters.bedrooms) &&
      (!filters.parkingSpaces || property.garage.quantity >= filters.parkingSpaces) &&
      (!filters.areaMin || property.livingArea >= filters.areaMin) &&
      (!filters.areaMax || property.livingArea <= filters.areaMax) &&
      (!filters.propertyType || property.propertyType.toLowerCase().includes(filters.propertyType.toLowerCase()));
    
    return matchesSearch && matchesStatus && matchesType && matchesFilters;
  });

  const saleProperties = properties.filter(p => p.isActive && (
    (p.listPrice && p.listPrice > 0) || p.transactionType === 'Venda' || p.transactionType === 'Venda/Aluguel'
  ));
  const rentProperties = properties.filter(p => p.isActive && (
    (p.rentalPrice && p.rentalPrice > 0) || p.transactionType === 'Aluguel' || p.transactionType === 'Venda/Aluguel'
  ));
  const activeProperties = properties.filter(p => p.isActive);
  const inactiveProperties = properties.filter(p => !p.isActive);

  const handleSave = async (property: Property) => {
    console.log(`🔄 PropertyList - handleSave: ${editingProperty ? 'EDIÇÃO' : 'CRIAÇÃO'} - `, property.title);
    console.log('📊 PropertyList - Dados da propriedade:', property);
    
    try {
      // Usar a função única de salvamento
      const savedProperty = await saveProperty(property);
      console.log('✅ PropertyList - Propriedade salva com sucesso:', savedProperty);
      
      toast({
        title: "Sucesso",
        description: `Imóvel ${editingProperty ? 'atualizado' : 'cadastrado'} com sucesso!`,
      });
      
      setShowForm(false);
      setEditingProperty(undefined);
      setSelectedPropertyType('');
      setSelectedCondominiumId(undefined);
      
      // NÃO chamar refreshProperties() - o estado já foi atualizado pela função saveProperty
      
    } catch (error: any) {
      console.error('❌ PropertyList - Erro ao salvar propriedade:', error);
      toast({
        title: "Erro",
        description: `Erro ao ${editingProperty ? 'atualizar' : 'cadastrar'} imóvel: ${error.message || error}`,
        variant: "destructive"
      });
    }
  };

  const handleEdit = (property: Property) => {
    setEditingProperty(property);
    setSelectedPropertyType(property.propertyType);
    setSelectedCondominiumId(property.condominiumId);
    setShowForm(true);
  };

  const handleNewProperty = () => {
    setShowTypeSelector(true);
  };

  const handleTypeSelect = (propertyType: string, condominiumId?: string) => {
    setSelectedPropertyType(propertyType);
    setSelectedCondominiumId(condominiumId);
    setShowTypeSelector(false);
    setShowForm(true);
  };

  const handleTogglePropertyStatus = async (property: Property) => {
    if (property.isActive) {
      // Inativar - mostrar modal
      setPropertyToDeactivate(property);
      setIsBulkAction(false);
      setShowDeactivateModal(true);
    } else {
      // Reativar diretamente
      await handleActivateProperty(property);
    }
  };

  const handleActivateProperty = async (property: Property) => {
    try {
      const { error } = await supabase
        .from('properties')
        .update({ 
          is_active: true,
          deactivation_reason: null,
          deactivation_date: null,
          deactivated_by: null
        })
        .eq('id', property.id);

      if (error) throw error;

      // Atualizar localmente
      const updatedProperty = { ...property, isActive: true };
      await updateProperty(updatedProperty);

      toast({
        title: 'Imóvel reativado',
        description: 'O imóvel voltará a aparecer no site e nos portais.'
      });

      // Garantir que os dados reflitam o banco após reativação
      await refreshProperties();
    } catch (error) {
      console.error('Erro ao reativar imóvel:', error);
      toast({
        title: 'Erro',
        description: 'Não foi possível reativar o imóvel.',
        variant: 'destructive'
      });
    }
  };

  const handleDeactivateProperty = async (reason: string, customReason?: string) => {
    if (!propertyToDeactivate) return;

    try {
      const finalReason = reason === 'outros' ? customReason : reason;
      const finalReasonStr = finalReason || '';
      
      if (isBulkAction) {
        // Ação em lote
        const propertiesToUpdate = Array.from(selectedProperties).map(id => 
          properties.find(p => p.listingId === id)
        ).filter(Boolean);

        for (const property of propertiesToUpdate) {
          if (!property) continue;
          
            const { error } = await supabase.rpc('deactivate_property', {
              p_property_id: property.id,
              p_reason: finalReasonStr,
              p_deactivated_by: (user?.id as string)
            });

          if (error) throw error;

          // Atualizar localmente
          const updatedProperty = { ...property, isActive: false };
          updateProperty(updatedProperty);
        }

        setSelectedProperties(new Set());
        toast({
          title: 'Imóveis inativados',
          description: `${propertiesToUpdate.length} imóveis foram inativados com sucesso.`
        });
      } else {
        // Ação individual
        const { error } = await supabase.rpc('deactivate_property', {
          p_property_id: propertyToDeactivate.id,
          p_reason: finalReasonStr,
          p_deactivated_by: (user?.id as string)
        });

        if (error) throw error;

        // Atualizar localmente
        const updatedProperty = { ...propertyToDeactivate, isActive: false };
        updateProperty(updatedProperty);

        toast({
          title: 'Imóvel inativado',
          description: 'O imóvel foi removido do site e dos portais.'
        });
      }
    } catch (error) {
      console.error('Erro ao inativar imóvel:', error);
      toast({
        title: 'Erro',
        description: 'Não foi possível inativar o imóvel.',
        variant: 'destructive'
      });
    } finally {
      setShowDeactivateModal(false);
      setPropertyToDeactivate(null);
      setIsBulkAction(false);
    }
  };

  const handleBulkDeactivate = () => {
    if (selectedProperties.size === 0) return;
    
    setPropertyToDeactivate(null);
    setIsBulkAction(true);
    setShowDeactivateModal(true);
  };

  const handleBulkActivate = async () => {
    if (selectedProperties.size === 0) return;

    try {
      const propertiesToUpdate = Array.from(selectedProperties).map(id => 
        properties.find(p => p.listingId === id)
      ).filter(Boolean);

      for (const property of propertiesToUpdate) {
        if (!property) continue;
        
        const { error } = await supabase
          .from('properties')
          .update({ 
            is_active: true,
            deactivation_reason: null,
            deactivation_date: null,
            deactivated_by: null
          })
          .eq('id', property.id);

        if (error) throw error;

        // Atualizar localmente
        const updatedProperty = { ...property, isActive: true };
        updateProperty(updatedProperty);
      }

      setSelectedProperties(new Set());
      toast({
        title: 'Imóveis reativados',
        description: `${propertiesToUpdate.length} imóveis foram reativados com sucesso.`
      });
    } catch (error) {
      console.error('Erro ao reativar imóveis:', error);
      toast({
        title: 'Erro',
        description: 'Não foi possível reativar os imóveis.',
        variant: 'destructive'
      });
    }
  };

  const handleDeleteProperty = async () => {
    if (!propertyToDelete && !isBulkDeleteAction) return;

    try {
      if (isBulkDeleteAction) {
        // Exclusão em lote
        const propertiesToDelete = Array.from(selectedProperties).map(id => 
          properties.find(p => p.listingId === id)
        ).filter(Boolean);

        let deletedCount = 0;
        let errorCount = 0;

        for (const property of propertiesToDelete) {
          if (!property) continue;
          
          try {
            const { error } = await supabase
              .from('properties')
              .delete()
              .eq('id', property.id);

            if (error) {
              console.error('Erro ao excluir imóvel:', property.listingId, error);
              errorCount++;
            } else {
              // Remover da lista local apenas se excluído com sucesso
              deleteProperty(property.listingId);
              deletedCount++;
            }
          } catch (err) {
            console.error('Erro ao excluir imóvel:', property.listingId, err);
            errorCount++;
          }
        }

        setSelectedProperties(new Set());

        // Mostrar apenas um toast final com o resultado
        if (deletedCount > 0 && errorCount === 0) {
          toast({
            title: 'Imóveis excluídos',
            description: `${deletedCount} imóveis foram excluídos com sucesso.`
          });
        } else if (deletedCount > 0 && errorCount > 0) {
          toast({
            title: 'Exclusão parcial',
            description: `${deletedCount} imóveis excluídos, ${errorCount} falharam.`,
            variant: 'destructive'
          });
        } else {
          toast({
            title: 'Erro na exclusão',
            description: `Não foi possível excluir nenhum dos ${errorCount} imóveis.`,
            variant: 'destructive'
          });
        }
      } else if (propertyToDelete) {
        // Exclusão individual
        const { error } = await supabase
          .from('properties')
          .delete()
          .eq('id', propertyToDelete.id);

        if (error) throw error;

        // Remover da lista local
        deleteProperty(propertyToDelete.listingId);

        toast({
          title: 'Imóvel excluído',
          description: 'O imóvel foi excluído permanentemente do sistema.'
        });
      }
    } catch (error) {
      console.error('Erro ao excluir imóvel:', error);
      toast({
        title: 'Erro',
        description: 'Não foi possível excluir o imóvel.',
        variant: 'destructive'
      });
    } finally {
      setShowDeleteModal(false);
      setPropertyToDelete(null);
      setIsBulkDeleteAction(false);
    }
  };

  const handleBulkDelete = () => {
    if (selectedProperties.size === 0) return;
    
    setPropertyToDelete(null);
    setIsBulkDeleteAction(true);
    setShowDeleteModal(true);
  };

  const handleSingleDelete = (property: Property) => {
    setPropertyToDelete(property);
    setIsBulkDeleteAction(false);
    setShowDeleteModal(true);
  };

  const handleSelectProperty = (listingId: string) => {
    const newSelected = new Set(selectedProperties);
    if (newSelected.has(listingId)) {
      newSelected.delete(listingId);
    } else {
      newSelected.add(listingId);
    }
    setSelectedProperties(newSelected);
  };

  const handleSelectAll = () => {
    if (selectedProperties.size === filteredProperties.length) {
      setSelectedProperties(new Set());
    } else {
      setSelectedProperties(new Set(filteredProperties.map(p => p.listingId)));
    }
  };

  const shareProperty = async (property: Property) => {
    const url = `${window.location.origin}/imovel/${property.listingId}`;
    const title = property.title || `Imóvel ${property.propertyCode}`;
    const text = `Confira este imóvel: ${title}`;
    try {
      if (navigator.share) {
        await navigator.share({ title, text, url });
        toast({ title: 'Compartilhado', description: 'Link compartilhado com sucesso!' });
      } else {
        await navigator.clipboard.writeText(url);
        toast({ title: 'Link copiado', description: 'URL do imóvel copiada para a área de transferência.' });
      }
    } catch (err) {
      try {
        await navigator.clipboard.writeText(url);
        toast({ title: 'Link copiado', description: 'URL do imóvel copiada para a área de transferência.' });
      } catch {
        alert(url);
      }
    }
  };

  const formatPrice = (price: number, context?: string) => {
    console.log(`💰 formatPrice (${context || 'unknown'}) chamado com valor:`, price, typeof price);
    if (!price || price === 0) {
      return '-';
    }
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
      minimumFractionDigits: 0
    }).format(price);
  };

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Header responsivo */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-3 sm:p-6">
        <div className="flex flex-col gap-3 sm:gap-4">
          <div>
            <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-900 mb-1 sm:mb-2">Gerenciar Imóveis</h1>
            <p className="text-gray-600 text-sm sm:text-base lg:text-lg">
              {filteredProperties.length} de {properties.length} imóveis cadastrados no sistema
            </p>
          </div>
        
          {/* Botões de ação reorganizados para mobile */}
          <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
            <div className="flex items-center gap-2 bg-gray-100 rounded-lg p-1 w-fit">
            <button
              onClick={() => setViewMode('grid')}
              className={`p-1.5 sm:p-2 rounded-md transition-colors ${
                viewMode === 'grid' 
                  ? 'bg-white text-orange-500 shadow-sm' 
                  : 'text-gray-500 hover:text-gray-700'
              }`}
              title="Visualização em Grade"
            >
              <Grid3X3 className="w-3 sm:w-4 h-3 sm:h-4" />
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`p-1.5 sm:p-2 rounded-md transition-colors ${
                viewMode === 'list' 
                  ? 'bg-white text-orange-500 shadow-sm' 
                  : 'text-gray-500 hover:text-gray-700'
              }`}
              title="Visualização em Lista"
            >
              <List className="w-3 sm:w-4 h-3 sm:h-4" />
            </button>
          </div>
          
          <div className="flex flex-1 sm:flex-none gap-2">
            <button
              onClick={() => setShowBulkImport(true)}
              className="flex-1 sm:flex-none bg-blue-500 text-white px-2 sm:px-4 py-2 rounded-lg hover:bg-blue-600 transition-colors flex items-center justify-center gap-1 sm:gap-2"
            >
              <Upload className="w-4 h-4" />
              <span className="text-xs sm:text-sm">Cadastrar em Lote</span>
            </button>
            <button
              onClick={handleNewProperty}
              className="flex-1 sm:flex-none bg-orange-500 text-white px-2 sm:px-4 py-2 rounded-lg hover:bg-orange-600 transition-colors flex items-center justify-center gap-1 sm:gap-2"
            >
              <Plus className="w-4 h-4" />
              <span className="text-xs sm:text-sm">Novo Imóvel</span>
            </button>
          </div>
          </div>
        </div>
      </div>

      {/* Abas de Filtros */}
      <div className="bg-white rounded-xl shadow-lg p-3 sm:p-6">
        <div className="flex flex-col gap-3 sm:gap-4 mb-4 sm:mb-6">
          {/* Status Tabs (Ativo/Inativo) */}
          <div className="flex flex-wrap gap-1 sm:gap-2 mb-3">
            <button
              onClick={() => setStatusTab('active')}
              className={`px-2 sm:px-4 py-1.5 sm:py-2 rounded-lg font-medium transition-colors text-xs sm:text-sm ${
                statusTab === 'active'
                  ? 'bg-green-500 text-white'
                  : 'text-gray-600 hover:text-gray-800 hover:bg-gray-100'
              }`}
            >
              Ativos ({activeProperties.length})
            </button>
            <button
              onClick={() => setStatusTab('inactive')}
              className={`px-2 sm:px-4 py-1.5 sm:py-2 rounded-lg font-medium transition-colors text-xs sm:text-sm ${
                statusTab === 'inactive'
                  ? 'bg-red-500 text-white'
                  : 'text-gray-600 hover:text-gray-800 hover:bg-gray-100'
              }`}
            >
              Inativos ({inactiveProperties.length})
            </button>
          </div>

          {/* Transaction Type Tabs (subordinado ao status) */}
          <div className="flex flex-wrap gap-1 sm:gap-2">
            <button
              onClick={() => setTypeTab('all')}
              className={`px-2 sm:px-4 py-1.5 sm:py-2 rounded-lg font-medium transition-colors text-xs sm:text-sm ${
                typeTab === 'all'
                  ? 'bg-blue-600 text-white'
                  : 'text-gray-600 hover:text-gray-800 hover:bg-gray-100'
              }`}
            >
              Todos ({statusTab === 'active' ? activeProperties.length : inactiveProperties.length})
            </button>
            <button
              onClick={() => setTypeTab('sale')}
              className={`px-2 sm:px-4 py-1.5 sm:py-2 rounded-lg font-medium transition-colors text-xs sm:text-sm ${
                typeTab === 'sale'
                  ? 'bg-orange-500 text-white'
                  : 'text-gray-600 hover:text-gray-800 hover:bg-gray-100'
              }`}
            >
              Venda ({statusTab === 'active' ? saleProperties.length : properties.filter(p => !p.isActive && ((p.listPrice && p.listPrice > 0) || p.transactionType === 'Venda' || p.transactionType === 'Venda/Aluguel')).length})
            </button>
            <button
              onClick={() => setTypeTab('rent')}
              className={`px-2 sm:px-4 py-1.5 sm:py-2 rounded-lg font-medium transition-colors text-xs sm:text-sm ${
                typeTab === 'rent'
                  ? 'bg-blue-500 text-white'
                  : 'text-gray-600 hover:text-gray-800 hover:bg-gray-100'
              }`}
            >
              Locação ({statusTab === 'active' ? rentProperties.length : properties.filter(p => !p.isActive && ((p.rentalPrice && p.rentalPrice > 0) || p.transactionType === 'Aluguel' || p.transactionType === 'Venda/Aluguel')).length})
            </button>
          </div>

          {/* Ações em Lote */}
          {selectedProperties.size > 0 && (
            <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 p-2 sm:p-3 bg-gray-50 rounded-lg">
              <span className="text-xs sm:text-sm text-gray-600">
                {selectedProperties.size} selecionado(s)
              </span>
              <div className="flex gap-2">
                {statusTab === 'active' ? (
                  <button
                    onClick={handleBulkDeactivate}
                    className="bg-red-500 text-white px-2 sm:px-3 py-1 sm:py-1.5 rounded-lg hover:bg-red-600 transition-colors flex items-center gap-1 text-xs sm:text-sm"
                  >
                    <PowerOff className="w-3 sm:w-4 h-3 sm:h-4" />
                    <span>Inativar</span>
                  </button>
                ) : (
                  <button
                    onClick={handleBulkActivate}
                    className="bg-green-500 text-white px-2 sm:px-3 py-1 sm:py-1.5 rounded-lg hover:bg-green-600 transition-colors flex items-center gap-1 text-xs sm:text-sm"
                  >
                    <Power className="w-3 sm:w-4 h-3 sm:h-4" />
                    <span>Ativar</span>
                  </button>
                )}
                <button
                  onClick={handleBulkDelete}
                  className="bg-red-700 text-white px-2 sm:px-3 py-1 sm:py-1.5 rounded-lg hover:bg-red-800 transition-colors flex items-center gap-1 text-xs sm:text-sm"
                >
                  <Trash2 className="w-3 sm:w-4 h-3 sm:h-4" />
                  <span>Excluir</span>
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Busca */}
        <div className="relative mb-4 sm:mb-6">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="Buscar por título, bairro, cidade ou código..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm"
          />
        </div>

        {/* Botão de Filtros */}
        <div className="mb-6">
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="flex items-center space-x-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
          >
            <Plus className={`w-4 h-4 transition-transform ${showFilters ? 'rotate-45' : ''}`} />
            <span>Filtros</span>
          </button>
        </div>

        {/* Filtros Avançados (Ocultos por padrão) */}
        {showFilters && (
          <div className="mb-6 p-4 bg-gray-50 rounded-lg">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 xl:grid-cols-6 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Preço Mín</label>
                <input
                  type="number"
                  placeholder="R$ 0"
                  value={filters.priceMin || ''}
                  onChange={(e) => setFilters(prev => ({ ...prev, priceMin: Number(e.target.value) }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Preço Máx</label>
                <input
                  type="number"
                  placeholder="R$ 999999"
                  value={filters.priceMax || ''}
                  onChange={(e) => setFilters(prev => ({ ...prev, priceMax: Number(e.target.value) }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Dormitórios</label>
                <select
                  value={filters.bedrooms || ''}
                  onChange={(e) => setFilters(prev => ({ ...prev, bedrooms: Number(e.target.value) }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                >
                  <option value="">Todos</option>
                  <option value="1">1+</option>
                  <option value="2">2+</option>
                  <option value="3">3+</option>
                  <option value="4">4+</option>
                  <option value="5">5+</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Vagas</label>
                <select
                  value={filters.parkingSpaces || ''}
                  onChange={(e) => setFilters(prev => ({ ...prev, parkingSpaces: Number(e.target.value) }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                >
                  <option value="">Todas</option>
                  <option value="1">1+</option>
                  <option value="2">2+</option>
                  <option value="3">3+</option>
                  <option value="4">4+</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Área Mín (m²)</label>
                <input
                  type="number"
                  placeholder="0"
                  value={filters.areaMin || ''}
                  onChange={(e) => setFilters(prev => ({ ...prev, areaMin: Number(e.target.value) }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Tipo</label>
                <select
                  value={filters.propertyType || ''}
                  onChange={(e) => setFilters(prev => ({ ...prev, propertyType: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                >
                  <option value="">Todos</option>
                  <option value="Apartment">Apartamento</option>
                  <option value="House">Casa</option>
                  <option value="Commercial">Comercial</option>
                  <option value="Land">Terreno</option>
                </select>
              </div>
            </div>

            {/* Limpar Filtros */}
            {(filters.priceMin || filters.priceMax || filters.bedrooms || filters.parkingSpaces || filters.areaMin || filters.propertyType) && (
              <div className="flex justify-end">
                <button
                  onClick={() => setFilters({
                    priceMin: 0,
                    priceMax: 0,
                    bedrooms: 0,
                    parkingSpaces: 0,
                    areaMin: 0,
                    areaMax: 0,
                    propertyType: ''
                  })}
                  className="text-sm text-gray-600 hover:text-gray-800 underline"
                >
                  Limpar todos os filtros
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Conteúdo principal */}
      {filteredProperties.length === 0 ? (
        <div className="bg-white rounded-xl shadow-lg p-12 text-center">
          <Search className="w-8 h-8 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-800 mb-2">
            Nenhum imóvel encontrado
          </h3>
          <p className="text-gray-600 mb-4">
            {searchTerm 
              ? 'Tente ajustar o termo de busca'
              : `Nenhum imóvel ${statusTab === 'active' ? 'ativo' : 'inativo'} encontrado`
            }
          </p>
          {statusTab === 'active' && (
            <button
              onClick={handleNewProperty}
              className="bg-orange-500 text-white px-6 py-3 rounded-lg hover:bg-orange-600 transition-colors"
            >
              Adicionar Imóvel
            </button>
          )}
        </div>
      ) : viewMode === 'list' ? (
        <div className="bg-white rounded-xl shadow-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 py-3 text-left">
                    <input
                      type="checkbox"
                      checked={selectedProperties.size === filteredProperties.length && filteredProperties.length > 0}
                      onChange={handleSelectAll}
                      className="h-4 w-4 text-orange-500 border-gray-300 rounded focus:ring-orange-500"
                    />
                  </th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Imóvel
                  </th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Localização
                  </th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Preço
                  </th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Captador
                  </th>
                  <th className="px-3 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Ações
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredProperties.map((property) => (
                  <tr key={property.listingId} className="hover:bg-gray-50">
                    <td className="px-3 py-4">
                      <input
                        type="checkbox"
                        checked={selectedProperties.has(property.listingId)}
                        onChange={() => handleSelectProperty(property.listingId)}
                        className="h-4 w-4 text-orange-500 border-gray-300 rounded focus:ring-orange-500"
                      />
                    </td>
                    <td className="px-3 py-4">
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={property.isActive}
                          onChange={() => handleTogglePropertyStatus(property)}
                          className="sr-only peer"
                        />
                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-orange-300 dark:peer-focus:ring-orange-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-green-600"></div>
                      </label>
                    </td>
                    <td className="px-3 py-4">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-12 w-12">
                          <img
                            className="h-12 w-12 rounded-lg object-cover"
                            src={property.media.images[0]?.url}
                            alt={property.title}
                          />
                        </div>
                        <div className="ml-3 flex-1 min-w-0">
                          <div className="text-sm font-medium text-gray-900 truncate max-w-[200px]" title={property.title}>
                            {property.title}
                          </div>
                          <div className="text-xs text-gray-500">
                            {property.propertyCode}
                          </div>
                          <div className="flex items-center space-x-3 text-xs text-gray-600 mt-1">
                            <div className="flex items-center">
                              <Bed className="w-3 h-3 mr-1" />
                              <span>{property.bedrooms}</span>
                            </div>
                            <div className="flex items-center">
                              <Car className="w-3 h-3 mr-1" />
                              <span>{property.garage.quantity}</span>
                            </div>
                            <div className="flex items-center">
                              <Ruler className="w-3 h-3 mr-1" />
                              <span>{property.livingArea}m²</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-3 py-4">
                      <div className="text-sm text-gray-900 truncate">
                        {property.location.neighborhood}
                      </div>
                      <div className="text-xs text-gray-500 truncate">
                        {property.location.city}
                      </div>
                    </td>
                    <td className="px-3 py-4">
                       {(() => {
                         const t = (property.transactionType || '').toString().toLowerCase();
                         console.log('🏷️ Property transactionType:', property.transactionType, 'listPrice:', property.listPrice, 'rentalPrice:', property.rentalPrice);
                         const isBoth = t === 'venda/aluguel' || t === 'both' || t === 'sale/rent';
                         return isBoth;
                       })() ? (
                         <div className="space-y-1">
                           <div className="text-sm font-bold text-green-600">
                             Venda: {formatPrice(property.listPrice, 'venda-combined')}
                           </div>
                            <div className="text-sm font-bold text-blue-600">
                              Locação: {formatPrice(property.rentalPrice, 'aluguel-combined')}
                            </div>
                         </div>
                       ) : (
                         <div className="text-base font-bold text-orange-600">
                           {(() => {
                             const t = (property.transactionType || '').toString().toLowerCase();
                             const isRent = t === 'aluguel' || t === 'for rent' || t === 'rent';
                             return isRent 
                               ? formatPrice(property.rentalPrice || 0, 'aluguel-only') 
                               : formatPrice(property.listPrice, 'venda-only');
                           })()}
                         </div>
                       )}
                      
                      {/* Condomínio e IPTU */}
                      <div className="flex flex-col space-y-0.5 text-xs text-gray-500 mt-1">
                        {property.propertyAdministrationFee && property.propertyAdministrationFee > 0 && (
                          <span>Cond: {formatPrice(property.propertyAdministrationFee)}</span>
                        )}
                        {property.yearlyTax && property.yearlyTax > 0 && (
                          <span>IPTU: {formatPrice(property.yearlyTax)}/mês</span>
                        )}
                      </div>
                      
                      <div className="text-xs text-gray-500 mt-1">
                        {new Date(property.listDate).toLocaleDateString('pt-BR')}
                      </div>
                    </td>
                    <td className="px-3 py-4">
                      {property.sendLeadsToQueue ? (
                        <div>
                          <div className="text-sm text-gray-900 truncate">Fila de distribuição</div>
                          <div className="text-xs text-gray-500">Leads deste imóvel seguem a ordem da fila</div>
                        </div>
                      ) : (
                        <>
                          <div className="text-sm text-gray-900 truncate">
                            {getUserById(property.responsibleBrokerId || property.userId)?.firstName} {getUserById(property.responsibleBrokerId || property.userId)?.lastName}
                          </div>
                          <div className="text-xs text-gray-500">
                            {getUserById(property.responsibleBrokerId || property.userId)?.phone ? 
                              formatPhone(getUserById(property.responsibleBrokerId || property.userId)?.phone || '') : 
                              'Telefone não informado'
                            }
                          </div>
                          {isCapturer(property) && (
                            <span className="text-xs bg-orange-100 text-orange-800 px-2 py-1 rounded-full mt-1 inline-block">
                              Seu imóvel
                            </span>
                          )}
                        </>
                      )}
                    </td>
                    <td className="px-3 py-4">
                      <div className="flex items-center justify-center space-x-1">
                        <button
                          onClick={() => setViewingProperty(property)}
                          className="text-blue-600 hover:text-blue-800 p-1 rounded transition-colors"
                          title="Visualizar"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => shareProperty(property)}
                          className="text-green-600 hover:text-green-800 p-1 rounded transition-colors"
                          title="Compartilhar"
                        >
                          <Share2 className="w-4 h-4" />
                        </button>
                        {canEditProperty(property) && (
                          <button
                            onClick={() => handleEdit(property)}
                            className="text-orange-600 hover:text-orange-800 p-1 rounded transition-colors"
                            title="Editar"
                          >
                            <Edit className="w-4 h-4" />
                          </button>
                        )}
                         {canDeleteProperty() && (
                           <button
                             onClick={() => handleSingleDelete(property)}
                             className="text-red-600 hover:text-red-800 p-1 rounded transition-colors"
                             title="Excluir"
                           >
                             <Trash2 className="w-4 h-4" />
                           </button>
                         )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        // Grid View
        <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
          {filteredProperties.map((property) => (
            <div key={property.listingId} className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow">
              {/* Header da card com checkbox e toggle */}
              <div className="p-4 pb-2 flex items-center justify-between">
                <input
                  type="checkbox"
                  checked={selectedProperties.has(property.listingId)}
                  onChange={() => handleSelectProperty(property.listingId)}
                  className="h-4 w-4 text-orange-500 border-gray-300 rounded focus:ring-orange-500"
                />
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={property.isActive}
                    onChange={() => handleTogglePropertyStatus(property)}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-orange-300 dark:peer-focus:ring-orange-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-green-600"></div>
                </label>
              </div>

              {/* Image */}
              <div className="relative h-48">
                <img
                  src={property.media.images[0]?.url}
                  alt={property.title}
                  className="w-full h-full object-cover"
                />
                {!property.isActive && (
                  <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center">
                    <span className="bg-red-500 text-white px-3 py-1 rounded-full text-sm font-medium">
                      INATIVO
                    </span>
                  </div>
                )}
              </div>

              {/* Content */}
              <div className="p-4">
                <div className="mb-2">
                  <h3 className="text-lg font-semibold text-gray-800 line-clamp-2 mb-1">
                    {property.title}
                  </h3>
                  <div className="flex items-center text-gray-600 text-sm">
                    <MapPin className="w-4 h-4 mr-1" />
                    <span>{property.location.neighborhood}, {property.location.city}</span>
                  </div>
                </div>

                <div className="flex items-center space-x-4 text-gray-600 text-sm mb-3">
                  <div className="flex items-center">
                    <Bed className="w-4 h-4 mr-1" />
                    <span>{property.bedrooms}</span>
                  </div>
                  <div className="flex items-center">
                    <Car className="w-4 h-4 mr-1" />
                    <span>{property.garage.quantity}</span>
                  </div>
                  <div className="flex items-center">
                    <Ruler className="w-4 h-4 mr-1" />
                    <span>{property.livingArea}m²</span>
                  </div>
                </div>

                <div className="flex items-center justify-between mb-4">
                  <div className="flex flex-col">
                    {property.transactionType === 'Venda/Aluguel' ? (
                      <div className="space-y-1">
                        <div className="text-lg font-bold text-green-600">
                          Venda: {formatPrice(property.listPrice)}
                        </div>
                        <div className="text-lg font-bold text-blue-600">
                          Locação: {formatPrice(property.rentalPrice || 0)}
                        </div>
                      </div>
                    ) : (
                      <div className="text-xl font-bold text-orange-600">
                        {property.transactionType === 'Aluguel' ? formatPrice(property.rentalPrice || 0) : formatPrice(property.listPrice)}
                      </div>
                    )}
                    
                    {/* Condomínio e IPTU */}
                    <div className="flex flex-col space-y-0.5 text-xs text-gray-500 mt-1">
                      {property.propertyAdministrationFee && property.propertyAdministrationFee > 0 && (
                        <span>Cond: {formatPrice(property.propertyAdministrationFee)}</span>
                      )}
                      {property.yearlyTax && property.yearlyTax > 0 && (
                        <span>IPTU: {formatPrice(property.yearlyTax)}/mês</span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center text-gray-500 text-xs">
                    <Calendar className="w-3 h-3 mr-1" />
                    <span>{new Date(property.listDate).toLocaleDateString('pt-BR')}</span>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex items-center justify-between">
                  <div className="text-xs text-gray-500">
                    ID: {property.listingId}
                  </div>
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => setViewingProperty(property)}
                      className="text-blue-600 hover:text-blue-800 p-1 rounded transition-colors"
                      title="Visualizar"
                    >
                      <Eye className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => shareProperty(property)}
                      className="text-green-600 hover:text-green-800 p-1 rounded transition-colors"
                      title="Compartilhar"
                    >
                      <Share2 className="w-4 h-4" />
                    </button>
                    {canEditProperty(property) && (
                      <button
                        onClick={() => handleEdit(property)}
                        className="text-orange-600 hover:text-orange-800 p-1 rounded transition-colors"
                        title="Editar"
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                    )}
                     {canDeleteProperty() && (
                       <button
                         onClick={() => handleSingleDelete(property)}
                         className="text-red-600 hover:text-red-800 p-1 rounded transition-colors"
                         title="Excluir"
                       >
                         <Trash2 className="w-4 h-4" />
                       </button>
                     )}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modals */}
      {showTypeSelector && (
        <PropertyTypeSelector
          onSelect={handleTypeSelect}
          onCancel={() => setShowTypeSelector(false)}
        />
      )}

      {showForm && (
        <PropertyForm
          property={editingProperty}
          initialPropertyType={selectedPropertyType}
          initialCondominiumId={selectedCondominiumId}
          onSave={handleSave}
          onCancel={() => {
            setShowForm(false);
            setEditingProperty(undefined);
            setSelectedPropertyType('');
            setSelectedCondominiumId(undefined);
          }}
        />
      )}

      {viewingProperty && (
        <PropertyDetailModal
          property={viewingProperty}
          onClose={() => setViewingProperty(undefined)}
        />
      )}

      {showDeactivateModal && (
        <DeactivatePropertyModal
          isOpen={showDeactivateModal}
          onClose={() => {
            setShowDeactivateModal(false);
            setPropertyToDeactivate(null);
            setIsBulkAction(false);
          }}
          onConfirm={handleDeactivateProperty}
          propertyTitle={
            isBulkAction 
              ? `${selectedProperties.size} imóveis selecionados`
              : propertyToDeactivate?.title || ''
          }
        />
      )}

      {showBulkImport && (
        <BulkPropertyImport
          onClose={() => setShowBulkImport(false)}
          onImportComplete={() => {
            // Refresh properties list
            window.location.reload();
          }}
        />
      )}

      {showDeleteModal && (
        <DeletePropertyModal
          isOpen={showDeleteModal}
          onClose={() => {
            setShowDeleteModal(false);
            setPropertyToDelete(null);
            setIsBulkDeleteAction(false);
          }}
          onConfirm={handleDeleteProperty}
          propertyTitle={
            isBulkDeleteAction 
              ? `${selectedProperties.size} imóveis selecionados`
              : propertyToDelete?.title || ''
          }
          isBulkAction={isBulkDeleteAction}
        />
      )}
    </div>
  );
  } catch (error) {
    console.error('🚨 PropertyList: Erro no componente:', error);
    throw error; // Re-throw para que o ErrorBoundary capture
  }
};

export default PropertyList;